import type { TicketedEventOrderStatus } from '@school-journey/shared-types'

/**
 * Mirrors `PAID_HOLD_TIMEOUT_MINUTES` in
 * `apps/backend/src/services/payments/release-expired-holds.ts` (currently 15
 * minutes). Not shared across the app/backend boundary today (no wire field
 * carries the hold deadline — see the file-level note in checkout-payment-step.tsx),
 * so this is a client-side approximation anchored to the moment the browser
 * received the order-creation response (a few ms after the server actually
 * placed the hold). Deliberately runs slightly AHEAD of the real deadline
 * (fails safe) rather than behind it.
 */
export const PAID_HOLD_TIMEOUT_MS = 15 * 60 * 1000

export interface PublicOrderStatusSnapshot {
  status: TicketedEventOrderStatus
}

/**
 * Fetches just the status of a public order by reference code + buyer email —
 * the same `GET /v1/ticketed-events/by-slug/:schoolSlug/orders/:referenceCode`
 * lookup the confirmation page uses (BE-TICKETS-002: buyer email required to
 * return anything). Used for (1) the confirmation page's pending→paid poll and
 * (2) the payment step's pre-retry seat-availability re-check. Returns null on
 * any failure (network, 404, malformed body) — callers treat that as
 * "unknown, keep current state" rather than a hard error.
 */
export async function fetchPublicOrderStatus(params: {
  schoolSlug: string
  referenceCode: string
  buyerEmail: string
  signal?: AbortSignal
}): Promise<PublicOrderStatusSnapshot | null> {
  const base = process.env.NEXT_PUBLIC_BACKEND_URL
  if (!base) return null
  const url = `${base}/v1/ticketed-events/by-slug/${encodeURIComponent(params.schoolSlug)}/orders/${encodeURIComponent(params.referenceCode)}?email=${encodeURIComponent(params.buyerEmail)}`
  try {
    const res = await fetch(url, { cache: 'no-store', signal: params.signal })
    if (!res.ok) return null
    const json = (await res.json()) as { data?: { status?: TicketedEventOrderStatus } }
    if (!json.data?.status) return null
    return { status: json.data.status }
  } catch {
    return null
  }
}

/** Formats a millisecond duration as `m:ss`, clamped at zero. */
export function formatCountdown(msRemaining: number): string {
  const clamped = Math.max(0, msRemaining)
  const totalSeconds = Math.floor(clamped / 1000)
  const minutes = Math.floor(totalSeconds / 60)
  const seconds = totalSeconds % 60
  return `${minutes}:${String(seconds).padStart(2, '0')}`
}
